import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { Award, Mail, FileSignature, FileKey2, CheckCircle2, XCircle, FileText, Loader2, RefreshCw, RotateCcw, Filter, ShieldCheck, Clock, History, Trash2, Search, ChevronDown, ChevronRight, Download, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAdminCertificates, useAdminTteBatches, useBatchCertificates, useGenerateLocalCertificates, useSendTteCertificates, useCheckTteStatus, useSendEmailCertificates, useDeleteCertificate, useResetTteCertificate, useCancelTteBatch, useDeleteTteBatch, exportAdminCertificates, previewBulkDeleteCertificates, useBulkDeleteCertificates, useRegistrationSearch, type BulkDeletePreviewResult, type BulkDeleteFilterParams } from "@/services/adminCertificateService";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { id } from "date-fns/locale";
import { usePeriods } from "@/services/usePeriods";
import { useAdminCertificateTypes } from "@/services/adminCertificateTypeService";
import { useAdminConferenceRooms } from "@/services/adminConferenceRoomService";

export default function CertificatesPage() {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [filterType, setFilterType] = useState<string>("all");
    const [filterCompleted, setFilterCompleted] = useState<string>("all");
    const [filterEmailed, setFilterEmailed] = useState<string>("all");
    const [filterPeriod, setFilterPeriod] = useState<string>("all");
    const [filterSearch, setFilterSearch] = useState<string>("");
    const [filterConferenceId, setFilterConferenceId] = useState<string>("all");
    const [filterRoomId, setFilterRoomId] = useState<string>("all");

    const [appliedFilters, setAppliedFilters] = useState({ type: "all", completed: "all", emailed: "all", period_id: "all", search: "", conference_id: "all", room_id: "all" });

    // Auto-select period
    const { data: periodsData } = usePeriods();
    const activePeriod = periodsData?.find((p: any) => p.is_active);
    const initialPeriodSet = useRef(false);

    useEffect(() => {
        if (activePeriod && !initialPeriodSet.current) {
            setFilterPeriod(String(activePeriod.period_id));
            setAppliedFilters(prev => ({ ...prev, period_id: String(activePeriod.period_id) }));
            initialPeriodSet.current = true;
        }
    }, [activePeriod]);

    // Modal State
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
    const [generateType, setGenerateType] = useState<string>("convention");
    const [generateConferenceId, setGenerateConferenceId] = useState<string>("all");
    const [generateLimit, setGenerateLimit] = useState<string>("");
    const [generateIds, setGenerateIds] = useState<string>("");
    const [generateForce, setGenerateForce] = useState<boolean>(false);
    const [generateRoomId, setGenerateRoomId] = useState<string>("all");
    const [generateMode, setGenerateMode] = useState<'bulk' | 'specific'>('bulk');
    const [generateSpecificSearch, setGenerateSpecificSearch] = useState('');
    const [generateSpecificSelectedIds, setGenerateSpecificSelectedIds] = useState<Set<number>>(new Set());
    const { data: generateSpecificData, isFetching: isGenerateSpecificSearching } = useRegistrationSearch(
        generateType,
        generateSpecificSearch,
        generateConferenceId
    );

    // Fetch Conferences
    const { data: conferencesData } = useQuery({
        queryKey: ["conferencesList"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/conference-billing/conferences");
            return data.data || data;
        },
    });

    const { data: roomsData } = useAdminConferenceRooms(generateConferenceId === 'all' ? undefined : generateConferenceId);
    const { data: filterRoomsData } = useAdminConferenceRooms(filterConferenceId === 'all' ? undefined : filterConferenceId);

    const { data: certificateTypes } = useAdminCertificateTypes();

    // Delete and Cancel Modal State
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [selectedCertIds, setSelectedCertIds] = useState<Set<number>>(new Set());
    const [bulkDeleteMode, setBulkDeleteMode] = useState<'selected' | 'filtered' | null>(null);
    const [bulkDeletePreview, setBulkDeletePreview] = useState<BulkDeletePreviewResult | null>(null);
    const [isBulkDeletePreviewLoading, setIsBulkDeletePreviewLoading] = useState(false);
    const bulkDeleteCertificates = useBulkDeleteCertificates();
    const [cancelBatchConfirmId, setCancelBatchConfirmId] = useState<number | null>(null);
    const [deleteBatchConfirmId, setDeleteBatchConfirmId] = useState<number | null>(null);
    const [emailConfirmState, setEmailConfirmState] = useState<"all" | number[] | { batchId: number } | null>(null);
    const [isSendTteModalOpen, setIsSendTteModalOpen] = useState(false);
    const [sendTteMode, setSendTteMode] = useState<'bulk' | 'specific'>('bulk');
    const [sendTteType, setSendTteType] = useState('convention');
    const [sendTteConferenceId, setSendTteConferenceId] = useState('all');
    const [sendTteRoomId, setSendTteRoomId] = useState('all');
    const [sendTteLimit, setSendTteLimit] = useState('50');
    const [sendTteSpecificSearch, setSendTteSpecificSearch] = useState('');
    const [sendTteSpecificSelectedIds, setSendTteSpecificSelectedIds] = useState<Set<number>>(new Set());
    const { data: sendTteSpecificData, isFetching: isSendTteSpecificSearching } = useAdminCertificates(
        1, 20, undefined, false, undefined, undefined,
        sendTteSpecificSearch || undefined, undefined, undefined, true
    );
    const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
    const [sendEmailMode, setSendEmailMode] = useState<'bulk' | 'specific'>('bulk');
    const [sendEmailType, setSendEmailType] = useState('all');
    const [sendEmailConferenceId, setSendEmailConferenceId] = useState('all');
    const [sendEmailRoomId, setSendEmailRoomId] = useState('all');
    const [sendEmailSpecificSearch, setSendEmailSpecificSearch] = useState('');
    const [sendEmailSpecificId, setSendEmailSpecificId] = useState<number | null>(null);
    const { data: sendEmailSpecificData, isFetching: isSendEmailSpecificSearching } = useAdminCertificates(
        1, 20, undefined, true, undefined, undefined,
        sendEmailSpecificSearch || undefined
    );
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportType, setExportType] = useState('all');
    const [exportConferenceId, setExportConferenceId] = useState('all');
    const [exportRoomId, setExportRoomId] = useState('all');
    const [exportIncludeSignedUrl, setExportIncludeSignedUrl] = useState(true);
    const [exportIncludeLocalUrl, setExportIncludeLocalUrl] = useState(false);
    const { data: tteRoomsData } = useAdminConferenceRooms(sendTteConferenceId === 'all' ? undefined : sendTteConferenceId);
    const { data: emailRoomsData } = useAdminConferenceRooms(sendEmailConferenceId === 'all' ? undefined : sendEmailConferenceId);
    const { data: exportRoomsData } = useAdminConferenceRooms(exportConferenceId === 'all' ? undefined : exportConferenceId);
    const [detailBatchId, setDetailBatchId] = useState<number | null>(null);

    // PDF Viewer Modal State
    const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
    const [selectedPdfCert, setSelectedPdfCert] = useState<any>(null);
    const [pdfTab, setPdfTab] = useState<string>("local");
    const [iframeLoading, setIframeLoading] = useState(true);

    useEffect(() => {
        setIframeLoading(true);
    }, [selectedPdfCert, pdfTab]);

    // Collapsible Table State
    const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

    const toggleRow = (id: number) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Fetch Data
    const { data: certData, isLoading: loadingCertificates, isFetching, refetch } = useAdminCertificates(
        page,
        perPage,
        appliedFilters.type === "all" ? undefined : appliedFilters.type,
        appliedFilters.completed === "all" ? undefined : appliedFilters.completed === "true",
        appliedFilters.emailed === "all" ? undefined : appliedFilters.emailed === "true",
        appliedFilters.period_id === "all" ? undefined : appliedFilters.period_id,
        appliedFilters.search === "" ? undefined : appliedFilters.search,
        appliedFilters.conference_id === "all" ? undefined : appliedFilters.conference_id,
        appliedFilters.room_id === "all" ? undefined : appliedFilters.room_id
    );

    const [batchPage, setBatchPage] = useState(1);
    const [batchPerPage, setBatchPerPage] = useState(10);
    const [batchSearch, setBatchSearch] = useState("");
    const [appliedBatchSearch, setAppliedBatchSearch] = useState("");
    const { data: batchData, isLoading: loadingBatches, isFetching: isFetchingBatches } = useAdminTteBatches(batchPage, batchPerPage, appliedBatchSearch);

    const [detailBatchPage, setDetailBatchPage] = useState(1);
    const [detailBatchPerPage, setDetailBatchPerPage] = useState(10);
    const [detailBatchSearch, setDetailBatchSearch] = useState("");
    const [appliedDetailBatchSearch, setAppliedDetailBatchSearch] = useState("");

    useEffect(() => {
        if (detailBatchId) {
            setDetailBatchPage(1);
            setDetailBatchSearch("");
            setAppliedDetailBatchSearch("");
        }
    }, [detailBatchId]);

    const { data: batchCertificatesData, isLoading: loadingBatchCertificates, isFetching: isFetchingBatchCertificates } = useBatchCertificates(
        detailBatchId,
        detailBatchPage,
        detailBatchPerPage,
        appliedDetailBatchSearch
    );

    // Mutations
    const generateLocal = useGenerateLocalCertificates();
    const sendTte = useSendTteCertificates();
    const deleteCertificate = useDeleteCertificate();
    const resetTteCertificate = useResetTteCertificate();
    const [resetTteConfirmId, setResetTteConfirmId] = useState<number | null>(null);
    const checkTteStatus = useCheckTteStatus();
    const sendEmail = useSendEmailCertificates();
    const cancelBatch = useCancelTteBatch();

    const handleCheckTteStatus = (tte_id: string) => {
        toast.promise(checkTteStatus.mutateAsync(tte_id), {
            loading: 'Mengecek status TTE...',
            success: 'Pengecekan TTE berhasil disinkronkan!',
            error: (err: any) => err?.message || 'Gagal mengecek status TTE.'
        });
    };

    const handleGenerateSubmit = async () => {
        if (!generateType) {
            toast.error("Tipe sertifikat wajib dipilih.");
            return;
        }

        let payloadType = generateType;
        let certificateTypeId = undefined;

        if (generateType.startsWith('speaker_')) {
            payloadType = 'speaker';
            certificateTypeId = generateType.split('_')[1];
        } else if (generateType.startsWith('event_guest_')) {
            // "event_guest" sendiri mengandung underscore, jadi TIDAK bisa dipecah dengan
            // split('_')[n] seperti speaker_<id>/conference_xxx_<id> di atas - itu akan memecah
            // jadi ['event','guest','<id>']. Pakai substring dengan prefix tetap.
            payloadType = 'event_guest';
            certificateTypeId = generateType.substring('event_guest_'.length);
        } else if (generateType.startsWith('conference_')) {
            payloadType = 'conference';
            if (generateType.includes('_')) {
                certificateTypeId = generateType.split('_')[2];
            }
        }

        if (generateMode === 'specific' && generateSpecificSelectedIds.size === 0) {
            toast.error("Pilih minimal satu peserta/pemakalah/speaker terlebih dahulu.");
            return;
        }

        const payload: any = {
            type: payloadType,
            limit: generateMode === 'specific' ? undefined : (generateLimit ? parseInt(generateLimit) : undefined),
            ids: generateMode === 'specific' ? Array.from(generateSpecificSelectedIds).join(',') : (generateIds ? generateIds : undefined),
            force: generateForce
        };

        if ((payloadType === 'conference' || payloadType === 'speaker') && generateConferenceId !== 'all') {
            payload.conference_id = generateConferenceId;
        }

        if (generateType.startsWith('conference_bestpresenter') && generateRoomId !== 'all') {
            payload.room_id = generateRoomId;
        }

        if (certificateTypeId) {
            payload.certificate_type_id = certificateTypeId;
        }

        try {
            const result = await generateLocal.mutateAsync(payload);
            if (result?.generated_count === 0) {
                toast(result?.warning || 'Tidak ada sertifikat yang dibuat: tidak ada peserta yang memenuhi syarat.', { icon: <TriangleAlert className="w-5 h-5 text-amber-500" /> });
            } else {
                const skippedText = result?.skipped_count ? `, ${result.skipped_count} dilewati (sudah ada)` : '';
                toast.success(`Berhasil membuat ${result?.generated_count ?? '?'} sertifikat baru${skippedText}.`);
            }
            setIsGenerateModalOpen(false);
            setGenerateSpecificSelectedIds(new Set());
        } catch (error: any) {
            setIsGenerateModalOpen(false);
            toast.error(error.message || "Gagal melakukan generate lokal.");
        }
    };

    const handleDelete = (id: number) => {
        setDeleteConfirmId(id);
    };

    const handleCancelBatch = (id: number) => {
        setCancelBatchConfirmId(id);
    };

    const deleteBatch = useDeleteTteBatch();
    const handleDeleteBatch = (batchId: number) => {
        setDeleteBatchConfirmId(batchId);
    };

    const handleSendTte = () => {
        setSendTteMode('bulk');
        setSendTteSpecificSearch('');
        setSendTteSpecificSelectedIds(new Set());
        setSendTteLimit('50');
        setIsSendTteModalOpen(true);
    };

    const confirmSendTte = () => {
        const periodId = filterPeriod !== 'all' ? filterPeriod : (activePeriod ? String(activePeriod.period_id) : null);
        if (!periodId) {
            toast.error("Pilih Periode terlebih dahulu pada filter halaman sebelum memproses TTE.");
            return;
        }

        if (sendTteMode === 'specific') {
            if (sendTteSpecificSelectedIds.size === 0) {
                toast.error("Pilih minimal satu sertifikat yang akan dikirim ke TTE.");
                return;
            }
            setIsSendTteModalOpen(false);
            toast.promise(
                sendTte.mutateAsync({ ids: Array.from(sendTteSpecificSelectedIds), period_id: Number(periodId) }),
                {
                    loading: 'Sedang memproses TTE...',
                    success: 'Antrean TTE berhasil dibuat!',
                    error: (err: any) => err?.message || 'Gagal memproses TTE.'
                }
            );
            return;
        }

        if (sendTteType === 'all') {
            toast.error("Pilih tipe sertifikat yang spesifik (tidak bisa 'Semua Tipe') untuk proses TTE.");
            return;
        }

        setIsSendTteModalOpen(false);

        let payloadType = sendTteType;
        let certificateTypeId = undefined;

        if (sendTteType.startsWith('speaker_')) {
            payloadType = 'speaker';
            certificateTypeId = sendTteType.split('_')[1];
        } else if (sendTteType.startsWith('event_guest_')) {
            payloadType = 'event_guest';
            certificateTypeId = sendTteType.substring('event_guest_'.length);
        } else if (sendTteType.startsWith('conference_')) {
            payloadType = 'conference';
            if (sendTteType.includes('_')) {
                certificateTypeId = sendTteType.split('_')[2];
            }
        }

        const payload: any = { all_completed: true, type: payloadType, period_id: Number(periodId) };
        if (sendTteConferenceId !== 'all') payload.conference_id = sendTteConferenceId;
        if (certificateTypeId) payload.certificate_type_id = certificateTypeId;
        if (sendTteType.startsWith('conference_bestpresenter') && sendTteRoomId !== 'all') {
            payload.room_id = sendTteRoomId;
        }
        if (sendTteLimit) payload.limit = parseInt(sendTteLimit);

        toast.promise(sendTte.mutateAsync(payload), {
            loading: 'Sedang memproses TTE...',
            success: 'Antrean TTE berhasil dibuat!',
            error: 'Gagal memproses TTE.'
        });
    };

    const handleSendEmail = () => {
        setSendEmailMode('bulk');
        setSendEmailSpecificSearch('');
        setSendEmailSpecificId(null);
        setIsSendEmailModalOpen(true);
    };

    const confirmSendEmail = () => {
        if (sendEmailMode === 'specific') {
            if (!sendEmailSpecificId) {
                toast.error("Pilih sertifikat yang akan dikirim terlebih dahulu.");
                return;
            }
            setIsSendEmailModalOpen(false);
            toast.promise(sendEmail.mutateAsync({ ids: [sendEmailSpecificId] }), {
                loading: 'Mengirim email...',
                success: 'Sertifikat berhasil dikirim!',
                error: (err: any) => err?.message || 'Gagal mengirim email.'
            });
            return;
        }

        setIsSendEmailModalOpen(false);

        let payloadType = sendEmailType === 'all' ? undefined : sendEmailType;
        let certificateTypeId = undefined;

        if (sendEmailType.startsWith('speaker_')) {
            payloadType = 'speaker';
            certificateTypeId = sendEmailType.split('_')[1];
        } else if (sendEmailType.startsWith('event_guest_')) {
            payloadType = 'event_guest';
            certificateTypeId = sendEmailType.substring('event_guest_'.length);
        } else if (sendEmailType.startsWith('conference_')) {
            payloadType = 'conference';
            if (sendEmailType.includes('_')) {
                certificateTypeId = sendEmailType.split('_')[2];
            }
        }

        const payload: any = { all_completed: true };
        if (payloadType) payload.type = payloadType;
        if (sendEmailConferenceId !== 'all') payload.conference_id = sendEmailConferenceId;
        if (certificateTypeId) payload.certificate_type_id = certificateTypeId;
        if (sendEmailType.startsWith('conference_bestpresenter') && sendEmailRoomId !== 'all') {
            payload.room_id = sendEmailRoomId;
        }

        toast.promise(sendEmail.mutateAsync(payload), {
            loading: 'Sedang memproses pengiriman email...',
            success: 'Antrean pengiriman email berhasil dibuat!',
            error: 'Gagal mengirim email.'
        });
    };

    const handleSendEmailClick = () => {
        handleSendEmail();
    };

    const confirmSendEmailSingle = () => {
        if (emailConfirmState === "all") {
            handleSendEmail();
        } else if (Array.isArray(emailConfirmState)) {
             toast.promise(sendEmail.mutateAsync({ ids: emailConfirmState }), {
                 loading: 'Mengirim email...',
                 success: 'Sertifikat berhasil dikirim!',
                 error: (err: any) => err?.message || 'Gagal mengirim email.'
             });
        } else if (emailConfirmState && "batchId" in emailConfirmState) {
            toast.promise(sendEmail.mutateAsync({ batch_id: emailConfirmState.batchId }), {
                loading: 'Mengirim ulang email ke seluruh batch...',
                success: 'Email sertifikat berhasil dikirim ulang!',
                error: (err: any) => err?.message || 'Gagal mengirim email.'
            });
        }
        setEmailConfirmState(null);
    };

    const [isExporting, setIsExporting] = useState(false);
    const confirmExport = async () => {
        if (!exportIncludeSignedUrl && !exportIncludeLocalUrl) {
            toast.error("Pilih minimal satu kolom URL sertifikat untuk disertakan.");
            return;
        }
        try {
            setIsExporting(true);
            await exportAdminCertificates(
                exportType === "all" ? undefined : exportType,
                undefined, // isCompleted
                undefined, // isEmailed
                filterPeriod === "all" ? undefined : filterPeriod,
                undefined, // search
                exportConferenceId === "all" ? undefined : exportConferenceId,
                exportRoomId === "all" ? undefined : exportRoomId,
                exportIncludeSignedUrl,
                exportIncludeLocalUrl
            );
            setIsExportModalOpen(false);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Gagal mengekspor data");
        } finally {
            setIsExporting(false);
        }
    };

    const handleFilterChange = (key: string, value: string) => {
        if (key === 'period_id') setFilterPeriod(value);
        if (key === 'type') setFilterType(value);
        if (key === 'completed') setFilterCompleted(value);
        if (key === 'emailed') setFilterEmailed(value);
        if (key === 'conference_id') setFilterConferenceId(value);
        if (key === 'room_id') setFilterRoomId(value);

        setAppliedFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPage(1);
    };

    const applyFilters = () => {
        setAppliedFilters({
            type: filterType,
            completed: filterCompleted,
            emailed: filterEmailed,
            period_id: filterPeriod,
            search: filterSearch,
            conference_id: filterConferenceId,
            room_id: filterRoomId
        });
        setPage(1);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            applyFilters();
        }, 500);
        return () => clearTimeout(timer);
    }, [filterSearch]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedBatchSearch(batchSearch);
            setBatchPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [batchSearch]);

    const getBulkDeleteFilterParams = (): BulkDeleteFilterParams => ({
        type: appliedFilters.type === 'all' ? undefined : appliedFilters.type,
        is_completed: appliedFilters.completed === 'all' ? undefined : appliedFilters.completed === 'true',
        is_emailed: appliedFilters.emailed === 'all' ? undefined : appliedFilters.emailed === 'true',
        period_id: appliedFilters.period_id === 'all' ? undefined : appliedFilters.period_id,
        search: appliedFilters.search || undefined,
        conference_id: appliedFilters.conference_id === 'all' ? undefined : appliedFilters.conference_id,
        room_id: appliedFilters.room_id === 'all' ? undefined : appliedFilters.room_id,
    });

    const toggleCertSelection = (id: number) => {
        setSelectedCertIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAllOnPage = () => {
        const pageIds: number[] = certData?.data?.map((c: any) => c.id) ?? [];
        const allSelected = pageIds.length > 0 && pageIds.every(id => selectedCertIds.has(id));
        setSelectedCertIds(prev => {
            const next = new Set(prev);
            if (allSelected) {
                pageIds.forEach(id => next.delete(id));
            } else {
                pageIds.forEach(id => next.add(id));
            }
            return next;
        });
    };

    const openBulkDeleteConfirm = async (mode: 'selected' | 'filtered') => {
        setBulkDeleteMode(mode);
        setBulkDeletePreview(null);
        setIsBulkDeletePreviewLoading(true);
        try {
            const preview = mode === 'selected'
                ? await previewBulkDeleteCertificates(Array.from(selectedCertIds))
                : await previewBulkDeleteCertificates(undefined, getBulkDeleteFilterParams());
            setBulkDeletePreview(preview);
        } catch (error: any) {
            toast.error(error?.response?.data?.error || "Gagal memuat pratinjau data yang akan dihapus.");
            setBulkDeleteMode(null);
        } finally {
            setIsBulkDeletePreviewLoading(false);
        }
    };

    const confirmBulkDelete = async () => {
        if (!bulkDeleteMode) return;
        try {
            const result = await bulkDeleteCertificates.mutateAsync(
                bulkDeleteMode === 'selected'
                    ? { ids: Array.from(selectedCertIds) }
                    : { filters: getBulkDeleteFilterParams() }
            );
            toast.success(result.message);
            setSelectedCertIds(new Set());
            setBulkDeleteMode(null);
            setBulkDeletePreview(null);
        } catch (error: any) {
            toast.error(error?.message || "Gagal menghapus sertifikat.");
        }
    };

    useEffect(() => {
        setSelectedCertIds(new Set());
    }, [appliedFilters, page]);

    const resetFilters = () => {
        setFilterType("all");
        setFilterCompleted("all");
        setFilterEmailed("all");
        if (activePeriod) {
            setFilterPeriod(String(activePeriod.period_id));
        } else {
            setFilterPeriod("all");
        }
        setFilterSearch("");
        setFilterConferenceId("all");
        setFilterRoomId("all");
        setAppliedFilters({
            type: "all",
            completed: "all",
            emailed: "all",
            period_id: activePeriod ? String(activePeriod.period_id) : "all",
            search: "",
            conference_id: "all",
            room_id: "all"
        });
        setPage(1);
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* 1. Header Card */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                {/* Dekorasi Background */}
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <Award className="w-64 h-64 text-indigo-600" />
                </div>

                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400 flex-shrink-0 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
                        <Award className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Sertifikat Peserta</h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
                            Kelola data sertifikat, cetak dokumen PDF, antrekan proses TTE, dan kirim via Email ke seluruh peserta secara otomatis.
                        </p>
                    </div>
                </div>
            </div>

            {/* 2. Timeline Alur Penerbitan */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 md:p-6 shadow-sm border border-slate-200 dark:border-slate-800">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex flex-col items-center md:items-start text-center md:text-left md:w-1/4">
                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-[0.2em] mb-1">PANDUAN ALUR</span>
                        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">Tahapan Penerbitan</h3>
                    </div>

                    <div className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full">
                        {/* Step 1 */}
                        <div className="flex-1 flex items-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-3 border border-slate-200 dark:border-slate-700 w-full">
                            <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shadow-sm border border-slate-200 dark:border-slate-600 flex-shrink-0 mr-3">
                                <FileKey2 className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                            </div>
                            <div>
                                <span className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Langkah 1</span>
                                <span className="block text-sm font-bold text-slate-800 dark:text-slate-200">Generate Lokal</span>
                            </div>
                        </div>

                        <div className="hidden sm:block text-slate-300 dark:text-slate-600">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                        </div>

                        {/* Step 2 */}
                        <div className="flex-1 flex items-center bg-indigo-50/50 dark:bg-indigo-900/20 rounded-2xl p-3 border border-indigo-100 dark:border-indigo-800/30 w-full relative overflow-hidden">
                            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center shadow-sm border border-indigo-200 dark:border-indigo-800 flex-shrink-0 mr-3 relative z-10">
                                <FileSignature className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div className="relative z-10">
                                <span className="block text-[10px] font-bold text-indigo-400 dark:text-indigo-500 uppercase tracking-wider mb-0.5">Langkah 2</span>
                                <span className="block text-sm font-bold text-indigo-900 dark:text-indigo-100">Proses TTE</span>
                            </div>
                            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-indigo-100/50 dark:from-indigo-900/20 to-transparent"></div>
                        </div>

                        <div className="hidden sm:block text-indigo-200 dark:text-indigo-900/50">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                        </div>

                        {/* Step 3 */}
                        <div className="flex-1 flex items-center bg-emerald-50/50 dark:bg-emerald-900/20 rounded-2xl p-3 border border-emerald-100 dark:border-emerald-800/30 w-full relative overflow-hidden">
                            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center shadow-sm border border-emerald-200 dark:border-emerald-800 flex-shrink-0 mr-3 relative z-10">
                                <Mail className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div className="relative z-10">
                                <span className="block text-[10px] font-bold text-emerald-400 dark:text-emerald-500 uppercase tracking-wider mb-0.5">Langkah 3</span>
                                <span className="block text-sm font-bold text-emerald-900 dark:text-emerald-100">Kirim Email</span>
                            </div>
                            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-emerald-100/50 dark:from-emerald-900/20 to-transparent"></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. Summary Cards */}
            {certData?.statistics && (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-slate-100 text-slate-600 rounded-lg"><Award className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Sertifikat</span>
                        </div>
                        <span className="text-3xl font-black text-slate-800 dark:text-white mt-1">{certData.statistics.total}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><Award className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Konvensi</span>
                        </div>
                        <span className="text-3xl font-black text-indigo-600 mt-1">{certData.statistics.convention}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><Award className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Konferensi</span>
                        </div>
                        <span className="text-3xl font-black text-indigo-600 mt-1">{certData.statistics.conference}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><Award className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Speaker</span>
                        </div>
                        <span className="text-3xl font-black text-indigo-600 mt-1">{certData.statistics.speaker || 0}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><Award className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Mainroom</span>
                        </div>
                        <span className="text-3xl font-black text-indigo-600 mt-1">{certData.statistics.mainroom || 0}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><Award className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Keynote</span>
                        </div>
                        <span className="text-3xl font-black text-indigo-600 mt-1">{certData.statistics.keynote || 0}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><Award className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Invited</span>
                        </div>
                        <span className="text-3xl font-black text-indigo-600 mt-1">{certData.statistics.invited_speaker || 0}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg"><Award className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Moderator</span>
                        </div>
                        <span className="text-3xl font-black text-indigo-600 mt-1">{certData.statistics.moderator || 0}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg"><ShieldCheck className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Selesai TTE</span>
                        </div>
                        <span className="text-3xl font-black text-emerald-600 mt-1">{certData.statistics.tte_completed}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-rose-300 dark:hover:border-rose-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-rose-50 text-rose-600 rounded-lg"><Clock className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Belum TTE</span>
                        </div>
                        <span className="text-3xl font-black text-rose-600 mt-1">{certData.statistics.tte_pending}</span>
                    </div>
                    <div className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-all hover:shadow-md hover:border-amber-300 dark:hover:border-amber-700">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1.5 bg-amber-50 text-amber-600 rounded-lg"><Mail className="w-4 h-4" /></div>
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Email Terkirim</span>
                        </div>
                        <span className="text-3xl font-black text-amber-600 mt-1">{certData.statistics.email_sent}</span>
                    </div>
                </div>
            )}

            {/* 3. Actions Row */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-end gap-4">
                <div className="flex flex-wrap items-center justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="h-11 px-5 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 flex-1 sm:flex-none"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button
                        onClick={() => setIsGenerateModalOpen(true)}
                        className="h-11 px-5 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white shadow-sm font-bold flex-1 sm:flex-none"
                    >
                        <FileKey2 className="w-4 h-4 mr-2" />
                        Generate Lokal
                    </Button>
                    <Button
                        onClick={handleSendTte}
                        disabled={sendTte.isPending}
                        className="h-11 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm font-bold flex-1 sm:flex-none"
                    >
                        {sendTte.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSignature className="w-4 h-4 mr-2" />}
                        Proses TTE
                    </Button>
                    <Button
                        onClick={handleSendEmailClick}
                        disabled={sendEmail.isPending}
                        className="h-11 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm font-bold flex-1 sm:flex-none"
                    >
                        {sendEmail.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                        Kirim Email
                    </Button>
                    <Button
                        onClick={() => setIsExportModalOpen(true)}
                        disabled={isExporting || isFetching}
                        className="h-11 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-bold flex-1 sm:flex-none"
                    >
                        {isExporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                        Export
                    </Button>
                    <div className="w-px h-8 bg-slate-200 dark:bg-slate-700 mx-1 hidden sm:block" />
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openBulkDeleteConfirm('filtered')}
                        disabled={isFetching}
                        title="Hapus Sesuai Filter"
                        className="h-11 w-11 shrink-0 rounded-xl border-rose-200 text-rose-600 bg-white dark:bg-slate-800 dark:border-rose-900/50 dark:text-rose-400 shadow-sm hover:bg-rose-50 dark:hover:bg-rose-900/20"
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {selectedCertIds.size > 0 && (
                <div className="flex items-center justify-between gap-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/50 rounded-2xl px-5 py-3 mt-4">
                    <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                        {selectedCertIds.size} sertifikat dipilih
                    </span>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedCertIds(new Set())} className="text-slate-500">
                            Batal Pilih
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => openBulkDeleteConfirm('selected')}
                            className="bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Hapus Terpilih
                        </Button>
                    </div>
                </div>
            )}

            {/* 4. Table Card */}
            <Tabs defaultValue="certificates" className="w-full mt-6">
                <TabsList className="mb-4 bg-slate-100 dark:bg-slate-800/50 p-1.5 rounded-xl w-full sm:w-auto flex overflow-x-auto border border-slate-200 dark:border-slate-700 justify-start h-auto">
                    <TabsTrigger value="certificates" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-6 py-2.5">Daftar Sertifikat</TabsTrigger>
                    <TabsTrigger value="history" className="rounded-lg font-bold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 data-[state=active]:shadow-sm px-6 py-2.5">Riwayat Antrean TTE</TabsTrigger>
                </TabsList>

                <TabsContent value="certificates" className="mt-0 outline-none focus:outline-none">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-300 dark:border-slate-800 overflow-hidden relative">

                {/* Table Header: Filters & Search */}
                <div className="p-5 md:p-6 flex flex-col gap-5 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800">

                    {/* Title & Search Row */}
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <h3 className="font-bold text-slate-900 dark:text-white text-lg">Daftar Sertifikat</h3>
                            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 px-3 py-1 rounded-full border border-slate-300 dark:border-slate-700 shadow-sm">
                                {certData?.statistics?.total || 0} Data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
                            <div className="w-full sm:w-[220px] shrink-0">
                                <Select value={filterPeriod} onValueChange={(val) => handleFilterChange('period_id', val)}>
                                    <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm font-semibold shadow-sm focus:ring-indigo-500">
                                        <SelectValue placeholder="Pilih Periode Aktif" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Semua Periode</SelectItem>
                                        {periodsData?.map((period: any) => (
                                            <SelectItem key={period.period_id} value={String(period.period_id)}>
                                                {period.name} {period.is_active ? '(Aktif)' : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="relative w-full sm:w-[300px]">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-4 w-4 text-slate-400" />
                                </div>
                                <Input
                                    type="text"
                                    placeholder="Cari nama / email / No Reg..."
                                    value={filterSearch}
                                    onChange={(e) => setFilterSearch(e.target.value)}
                                    className="pl-9 h-11 w-full bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 focus:border-indigo-500 rounded-xl transition-all shadow-sm text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Detailed Filters Row */}
                    <div className="flex flex-col sm:flex-row items-center gap-4 pt-5 border-t border-slate-200 dark:border-slate-700/60">
                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hidden sm:flex">
                            <Filter className="w-4 h-4" />
                            <span className="text-xs font-bold uppercase tracking-wider">FILTER:</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1 w-full">
                            <Select value={filterType} onValueChange={(val) => handleFilterChange('type', val)}>
                                <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm shadow-sm focus:ring-indigo-500">
                                    <SelectValue placeholder="Semua Tipe" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Semua Tipe</SelectItem>
                                    <SelectItem value="convention">Konvensi</SelectItem>
                                    <SelectItem value="conference">Konferensi (Peserta & Pemakalah)</SelectItem>
                                    <SelectItem value="mainroom">Mainroom / Plenary</SelectItem>
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PAPER') && (
                                        <SelectItem value={`conference_bestpaper_${certificateTypes.find((c: any) => c.code === 'BEST_PAPER')?.id}`}>
                                            Best Paper (Konferensi)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PRESENTER') && (
                                        <SelectItem value={`conference_bestpresenter_${certificateTypes.find((c: any) => c.code === 'BEST_PRESENTER')?.id}`}>
                                            Best Presenter (Konferensi)
                                        </SelectItem>
                                    )}
                                    <SelectItem value="speaker">Semua Speaker (Konferensi)</SelectItem>
                                    {certificateTypes?.filter((c: any) => c.code && c.source_type !== 'event_guest' && !['PRESENTER', 'NON_PRESENTER', 'BEST_PAPER', 'BEST_PRESENTER'].includes(c.code)).map((c: any) => (
                                        <SelectItem key={c.id} value={`speaker_${c.id}`}>{c.name} (Konferensi)</SelectItem>
                                    ))}
                                    <SelectItem value="event_guest">Semua Panitia & Tamu Konvensi</SelectItem>
                                    {certificateTypes?.filter((c: any) => c.source_type === 'event_guest').map((c: any) => (
                                        <SelectItem key={c.id} value={`event_guest_${c.id}`}>{c.name} (Konvensi)</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select value={filterCompleted} onValueChange={(val) => handleFilterChange('completed', val)}>
                                <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm shadow-sm focus:ring-indigo-500">
                                    <SelectValue placeholder="Semua Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Semua Status</SelectItem>
                                    <SelectItem value="true">Selesai TTE</SelectItem>
                                    <SelectItem value="false">Belum TTE</SelectItem>
                                </SelectContent>
                            </Select>

                            <Select value={filterEmailed} onValueChange={(val) => handleFilterChange('emailed', val)}>
                                <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm shadow-sm focus:ring-indigo-500">
                                    <SelectValue placeholder="Semua Email" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Semua Email</SelectItem>
                                    <SelectItem value="true">Sudah Terkirim</SelectItem>
                                    <SelectItem value="false">Belum Dikirim</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {(filterType === 'conference' || filterType.startsWith('conference_') || filterType === 'speaker' || filterType.startsWith('speaker_')) && (
                            <div className="w-full sm:w-[200px]">
                                <Select value={filterConferenceId} onValueChange={(val) => handleFilterChange('conference_id', val)}>
                                    <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm shadow-sm focus:ring-indigo-500">
                                        <SelectValue placeholder="Semua Track Konferensi" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Semua Track Konferensi</SelectItem>
                                        {conferencesData?.map((c: any) => (
                                            <SelectItem key={c.conference_id} value={String(c.conference_id)}>
                                                {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {filterType.startsWith('conference_bestpresenter') && (
                            <div className="w-full sm:w-[200px]">
                                <Select value={filterRoomId} onValueChange={(val) => handleFilterChange('room_id', val)}>
                                    <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm shadow-sm focus:ring-indigo-500">
                                        <SelectValue placeholder="Semua Room" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Semua Room</SelectItem>
                                        {filterRoomsData?.map((r: any) => (
                                            <SelectItem key={r.id} value={String(r.id)}>
                                                {r.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {(appliedFilters.type !== "all" || appliedFilters.completed !== "all" || appliedFilters.emailed !== "all" || appliedFilters.conference_id !== "all" || appliedFilters.room_id !== "all") && (
                            <Button
                                onClick={resetFilters}
                                variant="ghost"
                                className="h-11 px-4 rounded-xl text-sm text-rose-500 hover:text-rose-600 hover:bg-rose-50 font-bold"
                            >
                                Reset
                            </Button>
                        )}
                    </div>
                </div>
                {isFetching && !loadingCertificates && (
                    <div className="absolute inset-0 z-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                    </div>
                )}

                <div className="overflow-x-auto pb-4">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-5 border-b border-slate-100 dark:border-slate-800 w-10">
                                    <Checkbox
                                        checked={(certData?.data?.length ?? 0) > 0 && certData.data.every((c: any) => selectedCertIds.has(c.id))}
                                        onCheckedChange={() => toggleSelectAllOnPage()}
                                        aria-label="Pilih semua di halaman ini"
                                    />
                                </th>
                                <th className="px-4 py-5 border-b border-slate-100 dark:border-slate-800 w-10"></th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 w-16">ID</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Partisipan</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Tipe</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Status TTE</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Email</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">File</th>
                                <th className="px-4 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 bg-slate-50 dark:bg-slate-800 shadow-[-4px_0_6px_rgba(0,0,0,0.02)] z-10 w-16">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-600 dark:text-slate-300">
                            {loadingCertificates ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center justify-center text-slate-400">
                                            <Loader2 className="w-8 h-8 animate-spin mb-4 text-indigo-500" />
                                            <p className="font-medium text-sm">Memuat data sertifikat...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : certData?.data && certData.data.length > 0 ? (
                                certData.data.map((cert: any) => (
                                    <React.Fragment key={cert.id}>
                                        <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors group cursor-pointer" onClick={() => toggleRow(cert.id)}>
                                            <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedCertIds.has(cert.id)}
                                                    onCheckedChange={() => toggleCertSelection(cert.id)}
                                                    aria-label={`Pilih sertifikat #${cert.id}`}
                                                />
                                            </td>
                                            <td className="px-4 py-4 w-10 text-slate-400">
                                                {expandedRows[cert.id] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                                            </td>
                                            <td className="px-6 py-4 font-mono text-xs text-slate-400">
                                                #{cert.id}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-slate-900 dark:text-white text-sm">
                                                        {cert.type === 'convention'
                                                            ? cert.registration?.participant?.full_name
                                                            : cert.speaker
                                                                ? cert.speaker?.name
                                                                : cert.event_guest
                                                                    ? cert.event_guest?.full_name
                                                                    : cert.type === 'mainroom'
                                                                        ? cert.registration?.full_name || "Unknown"
                                                                        : cert.registration?.participant_name || "Unknown"}
                                                    </span>
                                                    <span className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                                        <Mail className="w-3 h-3" />
                                                        {cert.type === 'convention'
                                                            ? cert.registration?.participant?.email
                                                            : cert.speaker
                                                                ? (cert.speaker?.email || "Tidak ada email")
                                                                : cert.event_guest
                                                                    ? (cert.event_guest?.email || "Tidak ada email")
                                                                    : cert.registration?.email || "-"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 capitalize border border-slate-200 dark:border-slate-700">
                                                    {cert.certificate_type?.name || cert.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {cert.is_completed ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50">
                                                        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Selesai TTE
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800/50">
                                                        <Clock className="w-3.5 h-3.5 mr-1.5" /> Belum / Proses
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {cert.is_emailed ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50">
                                                        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Terkirim
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                                                        <XCircle className="w-3.5 h-3.5 mr-1.5" /> Tidak
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        onClick={(e) => { if(cert.local_file_url) { e.stopPropagation(); setSelectedPdfCert(cert); setPdfTab("local"); setPdfViewerOpen(true); } }} 
                                                        disabled={!cert.local_file_url}
                                                        className={`font-bold text-xs h-8 px-2 sm:px-3 ${cert.local_file_url ? 'text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50' : 'bg-orange-50 text-orange-400 border-orange-200/50 cursor-not-allowed opacity-70'}`}
                                                    >
                                                        <FileText className={`w-3.5 h-3.5 sm:mr-1.5 ${cert.local_file_url ? 'text-slate-500' : ''}`} />
                                                        <span className="hidden sm:inline">PDF Lokal</span>
                                                    </Button>

                                                    <Button 
                                                        variant="default" 
                                                        size="sm" 
                                                        onClick={(e) => { if(cert.signed_file_url) { e.stopPropagation(); setSelectedPdfCert(cert); setPdfTab("tte"); setPdfViewerOpen(true); } }} 
                                                        disabled={!cert.signed_file_url}
                                                        className={`font-bold text-xs h-8 px-2 sm:px-3 shadow-none ${cert.signed_file_url ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200' : 'bg-orange-50 text-orange-500 border border-orange-200 cursor-not-allowed opacity-80'}`}
                                                    >
                                                        <FileSignature className="w-3.5 h-3.5 sm:mr-1.5" />
                                                        <span className="hidden sm:inline">PDF TTE</span>
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right sticky right-0 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/50 shadow-[-4px_0_6px_rgba(0,0,0,0.02)] z-10 transition-colors" onClick={(e) => e.stopPropagation()}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="outline" size="sm" className="h-8 gap-1 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 border-slate-200 dark:border-slate-700">
                                                            <span>Aksi</span>
                                                            <ChevronDown className="h-3 w-3" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-48 rounded-xl shadow-lg border-slate-200 dark:border-slate-700">
                                                        <DropdownMenuItem 
                                                            disabled={!cert.signed_file_url && !cert.local_file_url}
                                                            onClick={() => setEmailConfirmState([cert.id])}
                                                            className="text-emerald-600 dark:text-emerald-400 focus:text-emerald-700 dark:focus:text-emerald-300 font-medium cursor-pointer"
                                                        >
                                                            <Mail className="w-4 h-4 mr-2 text-indigo-500" /> Kirim Email
                                                        </DropdownMenuItem>
                                                        {!!cert.signature_id && (
                                                            <DropdownMenuItem
                                                                disabled={resetTteCertificate.isPending}
                                                                onClick={() => setResetTteConfirmId(cert.id)}
                                                                className="text-sm font-medium text-amber-600 dark:text-amber-400 focus:text-amber-700 dark:focus:text-amber-300 cursor-pointer py-2.5"
                                                            >
                                                                <RotateCcw className="w-4 h-4 mr-2" /> Reset Status TTE
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            disabled={deleteCertificate.isPending || !!cert.signature_id}
                                                            onClick={() => handleDelete(cert.id)}
                                                            className="text-sm font-medium text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/20 cursor-pointer py-2.5"
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-2" /> Hapus
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>

                                        {expandedRows[cert.id] && (
                                            <tr className="bg-slate-50/60 dark:bg-slate-800/30">
                                                <td colSpan={9} className="px-6 py-6 border-b border-slate-100 dark:border-slate-800">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-10">

                                                        {cert.type === 'conference' && cert.registration?.conference && (
                                                            <div>
                                                                <span className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Track Konferensi</span>
                                                                <div className="text-sm font-bold text-slate-700 dark:text-slate-200 leading-relaxed border-l-2 border-indigo-400 pl-3">
                                                                    {cert.registration.conference.name}
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div>
                                                            <span className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">Waktu Dibuat</span>
                                                            <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                                                                {cert.created_at ? format(new Date(cert.created_at), "dd MMM yyyy HH:mm", { locale: id }) : "-"}
                                                            </span>
                                                        </div>

                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center justify-center text-slate-400">
                                            <ShieldCheck className="w-12 h-12 mb-4 text-slate-300 dark:text-slate-600" />
                                            <p className="font-semibold text-base text-slate-600 dark:text-slate-300">Tidak ada sertifikat</p>
                                            <p className="text-sm mt-1">Coba sesuaikan filter pencarian di atas.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {certData && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">
                                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{certData.from || 0} - {certData.to || 0}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{certData.total}</span> data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                    <option value="250">250</option>
                                    <option value="500">500</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="h-8 sm:h-9 px-3 sm:px-4 rounded-lg font-bold border-slate-200 dark:border-slate-700 text-xs sm:text-sm"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-1 sm:mr-2"><path d="m15 18-6-6 6-6" /></svg>
                                    Prev
                                </Button>
                                <span className="text-xs sm:text-sm font-bold px-2 sm:px-3 text-slate-700 dark:text-slate-300">
                                    {page} / {certData.last_page || 1}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.min(certData.last_page, p + 1))}
                                    disabled={page === (certData.last_page || 1)}
                                    className="h-8 sm:h-9 px-3 sm:px-4 rounded-lg font-bold border-slate-200 dark:border-slate-700 text-xs sm:text-sm"
                                >
                                    Next
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-1 sm:ml-2"><path d="m9 18 6-6-6-6" /></svg>
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            </TabsContent>

            {/* Riwayat Batch TTE Section */}
            <TabsContent value="history" className="mt-0 outline-none focus:outline-none">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <History className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Riwayat Antrean TTE</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Daftar batch dokumen yang sedang/telah diproses ke API TTE.</p>
                        </div>
                    </div>
                    
                    <div className="w-full sm:w-[250px] relative group">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors z-10" />
                        <Input
                            placeholder="Cari ID atau Deskripsi..."
                            value={batchSearch}
                            onChange={(e) => setBatchSearch(e.target.value)}
                            className="pl-9 h-10 w-full bg-slate-50 focus:bg-white dark:bg-slate-900/50 dark:focus:bg-slate-900 border-slate-200 dark:border-slate-700 focus:border-indigo-500 rounded-xl transition-all shadow-sm"
                        />
                    </div>
                </div>
                
                {isFetchingBatches && !loadingBatches && (
                    <div className="absolute inset-0 z-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                    </div>
                )}

                <div className="overflow-x-auto pb-4">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 w-16">ID Batch</th>
                                <th className="px-6 py-4 border-b border-slate-100 dark:border-slate-800">Status</th>
                                <th className="px-6 py-4 border-b border-slate-100 dark:border-slate-800">Keterangan</th>
                                <th className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 text-center">Total Dokumen</th>
                                <th className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 text-center">Berhasil</th>
                                <th className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 text-center">Gagal</th>
                                <th className="px-6 py-4 border-b border-slate-100 dark:border-slate-800">Tanggal Dibuat</th>
                                <th className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 bg-slate-50 dark:bg-slate-800 shadow-[-5px_0_10px_rgba(0,0,0,0.05)] z-10">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-600 dark:text-slate-300">
                            {loadingBatches ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-10 text-center text-slate-400">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-500" />
                                        <p className="text-sm">Memuat data riwayat...</p>
                                    </td>
                                </tr>
                            ) : batchData?.data && batchData.data.length > 0 ? (
                                batchData.data.map((batch: any) => (
                                    <tr key={batch.id} className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                                        <td className="px-6 py-4 font-mono text-xs text-slate-500">#{batch.id}</td>
                                        <td className="px-6 py-4">
                                            {batch.status === 'completed' ? (
                                                <span className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                    Selesai
                                                </span>
                                            ) : batch.status === 'processing' ? (
                                                <span className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                                    <Loader2 className="w-3 h-3 animate-spin mr-1" /> Proses
                                                </span>
                                            ) : batch.status === 'failed' ? (
                                                <span className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                                    Gagal
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200 uppercase">
                                                    {batch.status}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-xs font-semibold text-slate-700 dark:text-slate-200 max-w-[350px] whitespace-normal break-words">
                                            {batch.description || "-"}
                                        </td>
                                        <td className="px-6 py-4 text-center font-bold">{batch.total_documents}</td>
                                        <td className="px-6 py-4 text-center text-emerald-600 font-bold">{batch.completed_documents}</td>
                                        <td className="px-6 py-4 text-center text-rose-500 font-bold">{batch.failed_documents}</td>
                                        <td className="px-6 py-4 text-xs">
                                            {batch.created_at ? format(new Date(batch.created_at), "dd MMM yyyy HH:mm", { locale: id }) : "-"}
                                        </td>
                                        <td className="px-6 py-4 text-right sticky right-0 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/50 shadow-[-5px_0_10px_rgba(0,0,0,0.05)] z-10">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="outline" size="sm" className="h-8 gap-1 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 border-slate-200 dark:border-slate-700">
                                                        <span>More</span>
                                                        <ChevronDown className="h-3 w-3" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="w-40 rounded-xl z-[99] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl">
                                                    <DropdownMenuItem onClick={() => setDetailBatchId(batch.id)} className="cursor-pointer py-2.5">
                                                        Detail Batch
                                                    </DropdownMenuItem>
                                                    {batch.tte_id && (batch.status === 'processing' || batch.status === 'pending') && (
                                                        <DropdownMenuItem onClick={() => handleCheckTteStatus(batch.tte_id)} disabled={checkTteStatus.isPending} className="cursor-pointer py-2.5">
                                                            Cek Status
                                                        </DropdownMenuItem>
                                                    )}
                                                    {(batch.status === 'processing' || batch.status === 'pending') && (
                                                        <>
                                                            <DropdownMenuSeparator className="bg-slate-100 dark:bg-slate-800" />
                                                            <DropdownMenuItem onClick={() => handleCancelBatch(batch.id)} disabled={cancelBatch.isPending} className="cursor-pointer py-2.5 text-rose-600 focus:text-rose-700 focus:bg-rose-50 dark:focus:bg-rose-950/30">
                                                                Batalkan
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                    {(batch.status === 'completed' || batch.status === 'failed') && (
                                                        <>
                                                            <DropdownMenuSeparator className="bg-slate-100 dark:bg-slate-800" />
                                                            <DropdownMenuItem onClick={() => handleDeleteBatch(batch.id)} disabled={deleteBatch.isPending} className="cursor-pointer py-2.5 text-rose-600 focus:text-rose-700 focus:bg-rose-50 dark:focus:bg-rose-950/30">
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Hapus Riwayat
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="px-6 py-10 text-center text-slate-400">
                                        <p className="text-sm">Belum ada riwayat antrean TTE.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Batches */}
                {batchData && (
                    <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">
                                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{batchData.from || 0} - {batchData.to || 0}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{batchData.total}</span> data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={batchPerPage}
                                    onChange={(e) => { setBatchPerPage(Number(e.target.value)); setBatchPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setBatchPage((p) => Math.max(1, p - 1))}
                                    disabled={batchPage === 1}
                                    className="h-8 sm:h-9 px-3 sm:px-4 rounded-lg font-bold border-slate-200 dark:border-slate-700 text-xs sm:text-sm"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-1 sm:mr-2"><path d="m15 18-6-6 6-6" /></svg>
                                    Prev
                                </Button>
                                <span className="text-xs sm:text-sm font-bold px-2 sm:px-3 text-slate-700 dark:text-slate-300">
                                    {batchPage} / {batchData.last_page || 1}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setBatchPage((p) => Math.min(batchData.last_page, p + 1))}
                                    disabled={batchPage === (batchData.last_page || 1)}
                                    className="h-8 sm:h-9 px-3 sm:px-4 rounded-lg font-bold border-slate-200 dark:border-slate-700 text-xs sm:text-sm"
                                >
                                    Next
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-1 sm:ml-2"><path d="m9 18 6-6-6-6" /></svg>
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            </TabsContent>
            </Tabs>

            {/* Modal PDF Viewer */}
            <Dialog open={pdfViewerOpen} onOpenChange={setPdfViewerOpen}>
                <DialogContent showCloseButton={false} className="sm:max-w-[1400px] w-[95vw] h-[95vh] flex flex-col p-0 overflow-hidden bg-slate-100 dark:bg-slate-950 rounded-xl shadow-2xl">
                    <DialogHeader className="p-4 sm:px-6 sm:py-5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm z-10 flex flex-row justify-between items-start gap-4">
                        <div className="flex-1 text-left">
                            <DialogTitle className="text-xl text-slate-800 dark:text-slate-100">
                                Detail Dokumen Sertifikat
                            </DialogTitle>
                            <DialogDescription className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-slate-600 dark:text-slate-400">
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="font-semibold">Nama:</span> 
                                    <span className="text-slate-900 dark:text-slate-200">{selectedPdfCert?.participant_registration?.participant?.full_name || selectedPdfCert?.conference_registration?.participant_name || "-"}</span>
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="font-semibold">Email:</span> 
                                    <span className="text-slate-900 dark:text-slate-200">{selectedPdfCert?.participant_registration?.participant?.email || selectedPdfCert?.conference_registration?.email || "-"}</span>
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="font-semibold">No. Sertifikat:</span> 
                                    <span className="text-slate-900 dark:text-slate-200">{selectedPdfCert?.certificate_number || "-"}</span>
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="font-semibold">Status TTE:</span> 
                                    <span className="uppercase text-slate-900 dark:text-slate-200">{selectedPdfCert?.signature_status || "MENUNGGU"}</span>
                                </span>
                            </DialogDescription>
                        </div>
                        <div className="flex gap-2 mt-1">
                            {selectedPdfCert?.local_file_url && (
                                <Button variant="outline" size="sm" asChild className="rounded-lg shadow-sm font-semibold shrink-0">
                                    <a href={selectedPdfCert.local_file_url} target="_blank" rel="noopener noreferrer">
                                        Buka Lokal
                                    </a>
                                </Button>
                            )}
                            {selectedPdfCert?.signed_file_url && (
                                <Button variant="default" size="sm" asChild className="rounded-lg shadow-sm font-semibold shrink-0 bg-indigo-600 hover:bg-indigo-700">
                                    <a href={selectedPdfCert.signed_file_url} target="_blank" rel="noopener noreferrer">
                                        Buka TTE
                                    </a>
                                </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => setPdfViewerOpen(false)} className="rounded-lg shadow-sm font-semibold shrink-0 hidden sm:inline-flex">
                                Tutup
                            </Button>
                        </div>
                    </DialogHeader>
                    <div className="flex-1 flex flex-col relative bg-slate-200/50 dark:bg-slate-950/50 min-h-0">
                        {(selectedPdfCert?.local_file_url && selectedPdfCert?.signed_file_url) ? (
                            <Tabs value={pdfTab} onValueChange={setPdfTab} className="flex-1 flex flex-col h-full">
                                <div className="px-4 pt-4 bg-white dark:bg-slate-900 shrink-0">
                                    <TabsList className="w-full justify-start rounded-b-none border border-slate-200 dark:border-slate-800">
                                        <TabsTrigger value="local" className="px-6 data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800">PDF Lokal</TabsTrigger>
                                        <TabsTrigger value="tte" className="px-6 data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800">PDF TTE (Tertanda)</TabsTrigger>
                                    </TabsList>
                                </div>
                                <TabsContent value="local" className="flex-1 m-0 p-0 h-full w-full data-[state=inactive]:hidden data-[state=active]:flex relative">
                                    {iframeLoading && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 z-10">
                                            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-2" />
                                            <p className="text-sm text-slate-500 font-medium">Memuat Dokumen...</p>
                                        </div>
                                    )}
                                    <iframe 
                                        key={`local-${selectedPdfCert.id}`} 
                                        src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedPdfCert.local_file_url || '')}&embedded=true`} 
                                        className="w-full h-full border-0 flex-1 relative z-0" 
                                        title="Sertifikat Lokal"
                                        onLoad={() => setIframeLoading(false)}
                                    />
                                </TabsContent>
                                <TabsContent value="tte" className="flex-1 m-0 p-0 h-full w-full data-[state=inactive]:hidden data-[state=active]:flex relative">
                                    {iframeLoading && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 z-10">
                                            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-2" />
                                            <p className="text-sm text-slate-500 font-medium">Memuat Dokumen...</p>
                                        </div>
                                    )}
                                    <iframe 
                                        key={`tte-${selectedPdfCert.id}`} 
                                        src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedPdfCert.signed_file_url || '')}&embedded=true`} 
                                        className="w-full h-full border-0 flex-1 relative z-0" 
                                        title="Sertifikat TTE"
                                        onLoad={() => setIframeLoading(false)}
                                    />
                                </TabsContent>
                            </Tabs>
                        ) : (
                            <div className="flex-1 w-full h-full p-0 m-0 relative">
                                {iframeLoading && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 z-10">
                                        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-2" />
                                        <p className="text-sm text-slate-500 font-medium">Memuat Dokumen...</p>
                                    </div>
                                )}
                                <iframe 
                                    key={`single-${selectedPdfCert?.id}`}
                                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedPdfCert?.signed_file_url || selectedPdfCert?.local_file_url || '')}&embedded=true`} 
                                    className="w-full h-full border-0 relative z-0" 
                                    title="Sertifikat PDF"
                                    onLoad={() => setIframeLoading(false)}
                                />
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal Generate Lokal */}
            <Dialog open={isGenerateModalOpen} onOpenChange={setIsGenerateModalOpen}>
                <DialogContent className="sm:max-w-[550px] whitespace-normal overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Generate Sertifikat Lokal</DialogTitle>
                        <DialogDescription className="whitespace-normal leading-relaxed mt-2">
                            {generateMode === 'bulk'
                                ? 'Pilih tipe sertifikat yang akan digenerate. Jika data sangat banyak (misal Konferensi), pertimbangkan untuk mengisi batas maksimal (Limit) agar tidak terjadi timeout.'
                                : 'Cari dan pilih peserta/pemakalah/speaker spesifik (bisa lebih dari satu) yang sertifikatnya ingin digenerate.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
                            <button
                                type="button"
                                onClick={() => setGenerateMode('bulk')}
                                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${generateMode === 'bulk' ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                                Massal (Otomatis)
                            </button>
                            <button
                                type="button"
                                onClick={() => setGenerateMode('specific')}
                                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${generateMode === 'specific' ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                                Peserta Spesifik
                            </button>
                        </div>

                        <div className="grid gap-2 min-w-0">
                            <Label htmlFor="type" className="font-bold">Tipe Sertifikat *</Label>
                            <Select value={generateType} onValueChange={setGenerateType}>
                                <SelectTrigger id="type" className="min-w-0 w-full">
                                    <SelectValue placeholder="Pilih Tipe" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="convention">Konvensi</SelectItem>
                                    <SelectItem value="conference">Konferensi (Peserta & Pemakalah)</SelectItem>
                                    <SelectItem value="mainroom">Mainroom / Plenary Session</SelectItem>
                                    {certificateTypes?.find((c: any) => c.code === 'PRESENTER') && (
                                        <SelectItem value={`conference_presenter_${certificateTypes.find((c: any) => c.code === 'PRESENTER')?.id}`}>
                                            Konferensi - Presenter (Paksa)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.find((c: any) => c.code === 'NON_PRESENTER') && (
                                        <SelectItem value={`conference_nonpresenter_${certificateTypes.find((c: any) => c.code === 'NON_PRESENTER')?.id}`}>
                                            Konferensi - Non-Presenter (Paksa)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PAPER') && (
                                        <SelectItem value={`conference_bestpaper_${certificateTypes.find((c: any) => c.code === 'BEST_PAPER')?.id}`}>
                                            Best Paper (Konferensi)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PRESENTER') && (
                                        <SelectItem value={`conference_bestpresenter_${certificateTypes.find((c: any) => c.code === 'BEST_PRESENTER')?.id}`}>
                                            Best Presenter (Konferensi)
                                        </SelectItem>
                                    )}
                                    <SelectItem value="speaker">Semua Speaker (Konferensi)</SelectItem>
                                    {certificateTypes?.filter((c: any) => c.code && c.source_type !== 'event_guest' && !['PRESENTER', 'NON_PRESENTER', 'BEST_PAPER', 'BEST_PRESENTER'].includes(c.code)).map((c: any) => (
                                        <SelectItem key={c.id} value={`speaker_${c.id}`}>{c.name} (Konferensi)</SelectItem>
                                    ))}
                                    <SelectItem value="event_guest">Semua Panitia & Tamu Konvensi</SelectItem>
                                    {certificateTypes?.filter((c: any) => c.source_type === 'event_guest').map((c: any) => (
                                        <SelectItem key={c.id} value={`event_guest_${c.id}`}>{c.name} (Konvensi)</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {(generateType.startsWith('conference_presenter') || generateType.startsWith('conference_nonpresenter')) && (
                                <span className="text-xs text-amber-600 dark:text-amber-400 whitespace-normal leading-relaxed">
                                    "Paksa" akan menerbitkan sertifikat dengan peran ini apa pun status is_presenter peserta di sistem - cocok untuk peserta yang sebenarnya ikut presentasi (mis. anggota tim paper) meski terdaftar Non-Presenter, atau sebaliknya. Gunakan mode "Peserta Spesifik" di bawah supaya tidak salah menimpa peserta lain.
                                </span>
                            )}
                        </div>

                        {(generateType.startsWith('conference') || generateType.startsWith('speaker') || generateType === 'speaker') && (
                            <div className="grid gap-2 min-w-0">
                                <Label htmlFor="conference" className="font-bold">Track Konferensi (Opsional)</Label>
                                <Select value={generateConferenceId} onValueChange={setGenerateConferenceId}>
                                    <SelectTrigger id="conference" className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all">
                                        <SelectValue placeholder="Semua Track Konferensi" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Semua Track Konferensi</SelectItem>
                                        {conferencesData?.map((c: any) => (
                                            <SelectItem key={c.conference_id} value={String(c.conference_id)}>
                                                {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {generateType.startsWith('conference_bestpresenter') && (
                            <div className="grid gap-2 min-w-0">
                                <Label htmlFor="room" className="font-bold">Room (Opsional)</Label>
                                <Select value={generateRoomId} onValueChange={setGenerateRoomId}>
                                    <SelectTrigger id="room" className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all">
                                        <SelectValue placeholder="Semua Room" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Semua Room</SelectItem>
                                        {roomsData?.map((r: any) => (
                                            <SelectItem key={r.id} value={String(r.id)}>
                                                {r.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {generateMode === 'bulk' ? (
                            <>
                                <div className="grid gap-2">
                                    <Label htmlFor="limit" className="font-bold">Limit / Batas Maksimal</Label>
                                    <Input
                                        id="limit"
                                        type="number"
                                        placeholder="Contoh: 100"
                                        value={generateLimit}
                                        onChange={(e) => setGenerateLimit(e.target.value)}
                                    />
                                    <span className="text-xs text-slate-500 whitespace-normal leading-relaxed">Kosongkan jika ingin mencetak semua secara otomatis (butuh waktu lama).</span>
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="ids" className="font-bold">ID Registrasi Spesifik (Opsional)</Label>
                                    <Input
                                        id="ids"
                                        placeholder="Contoh: 15,16,17"
                                        value={generateIds}
                                        onChange={(e) => setGenerateIds(e.target.value)}
                                    />
                                    <span className="text-xs text-slate-500 whitespace-normal leading-relaxed">Gunakan koma untuk memisahkan ID. Jika diisi, sistem hanya men-generate ID tersebut.</span>
                                </div>
                            </>
                        ) : (
                            <div className="grid gap-2 min-w-0">
                                <Label className="font-bold">Cari Peserta/Pemakalah/Speaker *</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        value={generateSpecificSearch}
                                        onChange={(e) => setGenerateSpecificSearch(e.target.value)}
                                        placeholder="Ketik nama, email, atau nomor registrasi..."
                                        className="pl-9 h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl"
                                    />
                                </div>
                                <div className="max-h-64 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl divide-y divide-slate-100 dark:divide-slate-800">
                                    {isGenerateSpecificSearching ? (
                                        <div className="p-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" /> Mencari...
                                        </div>
                                    ) : generateSpecificData && generateSpecificData.length > 0 ? (
                                        generateSpecificData.map((item) => {
                                            const isSelected = generateSpecificSelectedIds.has(item.id);
                                            return (
                                                <label
                                                    key={item.id}
                                                    className={`flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onCheckedChange={() => {
                                                            setGenerateSpecificSelectedIds(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="font-semibold text-slate-800 dark:text-slate-200 truncate">{item.name || 'Tanpa Nama'}</div>
                                                        <div className="text-xs text-slate-500 truncate">{item.email || '-'}{item.extra ? ` · ${item.extra}` : ''}</div>
                                                    </div>
                                                </label>
                                            );
                                        })
                                    ) : (
                                        <div className="p-4 text-center text-sm text-slate-400">Tidak ada hasil yang cocok.</div>
                                    )}
                                </div>
                                {generateSpecificSelectedIds.size > 0 && (
                                    <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{generateSpecificSelectedIds.size} dipilih</span>
                                )}
                            </div>
                        )}

                        <div className="flex items-start gap-3 mt-2">
                            <input
                                type="checkbox"
                                id="forceRegenerate"
                                className="w-4 h-4 mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-700"
                                checked={generateForce}
                                onChange={(e) => setGenerateForce(e.target.checked)}
                            />
                            <div className="grid gap-1.5 leading-none">
                                <label
                                    htmlFor="forceRegenerate"
                                    className="text-sm font-bold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                >
                                    Timpa File Lama (Force Regenerate)
                                </label>
                                <p className="text-xs text-slate-500 whitespace-normal leading-relaxed">
                                    Centang opsi ini jika file sertifikat sudah pernah digenerate namun Anda ingin membuatnya ulang (mengganti file PDF lama dengan yang baru).
                                </p>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsGenerateModalOpen(false)} disabled={generateLocal.isPending}>
                            Batal
                        </Button>
                        <Button
                            onClick={handleGenerateSubmit}
                            disabled={generateLocal.isPending || (generateMode === 'specific' && generateSpecificSelectedIds.size === 0)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            {generateLocal.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            {generateLocal.isPending ? "Sedang Memproses..." : "Mulai Generate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Konfirmasi Hapus Sertifikat */}
            <Dialog open={deleteConfirmId !== null} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-rose-600 flex items-center gap-2">
                            <Trash2 className="w-5 h-5" />
                            Hapus Sertifikat
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            Apakah Anda yakin ingin menghapus dokumen sertifikat secara permanen? Data yang telah dihapus tidak dapat dikembalikan.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button
                            variant="destructive"
                            className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={() => {
                                if (deleteConfirmId !== null) {
                                    toast.promise(deleteCertificate.mutateAsync(deleteConfirmId), {
                                        loading: 'Menghapus sertifikat...',
                                        success: 'Sertifikat berhasil dihapus!',
                                        error: 'Gagal menghapus sertifikat.'
                                    });
                                }
                                setDeleteConfirmId(null);
                            }}
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Hapus Sertifikat
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Konfirmasi Reset Status TTE */}
            <Dialog open={resetTteConfirmId !== null} onOpenChange={(open) => !open && setResetTteConfirmId(null)}>
                <DialogContent className="sm:max-w-[450px]">
                    <DialogHeader>
                        <DialogTitle className="text-amber-600 flex items-center gap-2">
                            <RotateCcw className="w-5 h-5" />
                            Reset Status TTE
                        </DialogTitle>
                        <DialogDescription className="pt-2 leading-relaxed">
                            Status TTE sertifikat ini akan direset (kembali ke "Belum TTE") <strong>khusus untuk sertifikat ini saja</strong> — sertifikat lain dalam batch yang sama tidak akan terpengaruh.
                            <br /><br />
                            Ini hanya mengubah catatan di sistem kita. Dokumen lama yang sudah ditandatangani tetap tersimpan sebagai riwayat di sistem TTE. Setelah direset, Anda bisa generate ulang (Force) dan mengajukan TTE ulang khusus untuk sertifikat ini.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setResetTteConfirmId(null)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button
                            className="rounded-xl font-bold bg-amber-600 hover:bg-amber-700 text-white"
                            disabled={resetTteCertificate.isPending}
                            onClick={() => {
                                if (resetTteConfirmId !== null) {
                                    toast.promise(resetTteCertificate.mutateAsync(resetTteConfirmId), {
                                        loading: 'Mereset status TTE...',
                                        success: (result: any) => result?.message || 'Status TTE berhasil direset!',
                                        error: (err: any) => err?.message || 'Gagal mereset status TTE.'
                                    });
                                }
                                setResetTteConfirmId(null);
                            }}
                        >
                            {resetTteCertificate.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                            Reset Status TTE
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Konfirmasi Hapus Massal (Terpilih / Sesuai Filter) */}
            <Dialog open={bulkDeleteMode !== null} onOpenChange={(open) => !open && setBulkDeleteMode(null)}>
                <DialogContent className="sm:max-w-[450px]">
                    <DialogHeader>
                        <DialogTitle className="text-rose-600 flex items-center gap-2">
                            <Trash2 className="w-5 h-5" />
                            {bulkDeleteMode === 'selected' ? 'Hapus Sertifikat Terpilih' : 'Hapus Sertifikat Sesuai Filter'}
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            {bulkDeleteMode === 'filtered'
                                ? 'Semua sertifikat yang cocok dengan filter yang sedang aktif di halaman ini akan dihapus permanen.'
                                : 'Sertifikat yang Anda pilih akan dihapus permanen.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-2">
                        {isBulkDeletePreviewLoading ? (
                            <div className="flex items-center justify-center gap-2 text-slate-500 py-4">
                                <Loader2 className="w-5 h-5 animate-spin" /> Memuat pratinjau...
                            </div>
                        ) : bulkDeletePreview ? (
                            <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-2 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-slate-500">Total cocok dengan kriteria</span>
                                    <span className="font-bold text-slate-800 dark:text-slate-200">{bulkDeletePreview.total_matched}</span>
                                </div>
                                <div className="flex items-center justify-between text-rose-600 dark:text-rose-400">
                                    <span>Akan dihapus</span>
                                    <span className="font-bold">{bulkDeletePreview.deletable_count}</span>
                                </div>
                                {bulkDeletePreview.locked_count > 0 && (
                                    <div className="flex items-center justify-between text-amber-600 dark:text-amber-400">
                                        <span>Dilewati (sudah/sedang TTE)</span>
                                        <span className="font-bold">{bulkDeletePreview.locked_count}</span>
                                    </div>
                                )}
                                {bulkDeletePreview.deletable_count === 0 && (
                                    <p className="text-xs text-slate-400 pt-1">Tidak ada sertifikat yang dapat dihapus (semua sudah/sedang dalam antrean TTE).</p>
                                )}
                            </div>
                        ) : null}
                    </div>

                    <DialogFooter className="mt-2 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setBulkDeleteMode(null)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={isBulkDeletePreviewLoading || bulkDeleteCertificates.isPending || !bulkDeletePreview || bulkDeletePreview.deletable_count === 0}
                            className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={confirmBulkDelete}
                        >
                            {bulkDeleteCertificates.isPending ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Hapus {bulkDeletePreview ? bulkDeletePreview.deletable_count : ''} Sertifikat
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel Batch Confirmation Modal */}
            <Dialog open={cancelBatchConfirmId !== null} onOpenChange={(open) => !open && setCancelBatchConfirmId(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Batalkan Batch TTE</DialogTitle>
                        <DialogDescription className="text-slate-500 dark:text-slate-400 mt-2">
                            Apakah Anda yakin ingin membatalkan batch ini? Pembatalan akan dikirim ke sistem TTE dan sertifikat-sertifikat yang ada di dalamnya akan dilepas dari antrean. Jika statusnya menyangkut/gagal, antrean akan dibatalkan secara lokal.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setCancelBatchConfirmId(null)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Tutup
                        </Button>
                        <Button
                            variant="destructive"
                            className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={() => {
                                if (cancelBatchConfirmId !== null) {
                                    toast.promise(cancelBatch.mutateAsync(cancelBatchConfirmId), {
                                        loading: 'Membatalkan batch TTE...',
                                        success: 'Batch berhasil dibatalkan!',
                                        error: 'Gagal membatalkan batch.'
                                    });
                                }
                                setCancelBatchConfirmId(null);
                            }}
                        >
                            <XCircle className="w-4 h-4 mr-2" />
                            Batalkan Batch
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Send TTE Modal */}
            <Dialog open={isSendTteModalOpen} onOpenChange={setIsSendTteModalOpen}>
                <DialogContent className="sm:max-w-[550px] whitespace-normal overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Proses Tanda Tangan Elektronik (TTE)</DialogTitle>
                        <DialogDescription className="text-slate-500 dark:text-slate-400 mt-2">
                            {sendTteMode === 'bulk'
                                ? 'Pilih tipe sertifikat yang akan diproses TTE secara massal (maksimal 50 dokumen per proses).'
                                : 'Cari dan pilih sertifikat spesifik (yang sudah digenerate, belum dikirim TTE) yang akan diproses.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
                            <button
                                type="button"
                                onClick={() => setSendTteMode('bulk')}
                                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${sendTteMode === 'bulk' ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                                Massal (Berdasarkan Tipe)
                            </button>
                            <button
                                type="button"
                                onClick={() => setSendTteMode('specific')}
                                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${sendTteMode === 'specific' ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                                Sertifikat Spesifik
                            </button>
                        </div>

                        {sendTteMode === 'specific' ? (
                            <div className="grid gap-2 min-w-0">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Cari Sertifikat *</label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        value={sendTteSpecificSearch}
                                        onChange={(e) => setSendTteSpecificSearch(e.target.value)}
                                        placeholder="Ketik nama, email, atau nomor sertifikat..."
                                        className="pl-9 h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl"
                                    />
                                </div>
                                <div className="max-h-64 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl divide-y divide-slate-100 dark:divide-slate-800">
                                    {isSendTteSpecificSearching ? (
                                        <div className="p-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" /> Mencari...
                                        </div>
                                    ) : sendTteSpecificData?.data && sendTteSpecificData.data.length > 0 ? (
                                        sendTteSpecificData.data.map((cert: any) => {
                                            const name = cert.type === 'convention'
                                                ? cert.registration?.participant?.full_name
                                                : cert.speaker
                                                    ? cert.speaker?.name
                                                    : cert.event_guest
                                                        ? cert.event_guest?.full_name
                                                        : cert.type === 'mainroom'
                                                            ? cert.registration?.full_name
                                                            : cert.registration?.participant_name;
                                            const email = cert.type === 'convention'
                                                ? cert.registration?.participant?.email
                                                : cert.speaker
                                                    ? cert.speaker?.email
                                                    : cert.event_guest
                                                        ? cert.event_guest?.email
                                                        : cert.registration?.email;
                                            const isSelected = sendTteSpecificSelectedIds.has(cert.id);
                                            return (
                                                <label
                                                    key={cert.id}
                                                    className={`flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onCheckedChange={() => {
                                                            setSendTteSpecificSelectedIds(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(cert.id)) next.delete(cert.id); else next.add(cert.id);
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="font-semibold text-slate-800 dark:text-slate-200 truncate">{name || 'Tanpa Nama'}</div>
                                                        <div className="text-xs text-slate-500 truncate">{email || '-'} &middot; {cert.certificate_type?.name || cert.type}</div>
                                                    </div>
                                                </label>
                                            );
                                        })
                                    ) : (
                                        <div className="p-4 text-center text-sm text-slate-400">
                                            Tidak ada sertifikat yang cocok (hanya yang sudah digenerate dan belum dikirim TTE yang ditampilkan).
                                        </div>
                                    )}
                                </div>
                                {sendTteSpecificSelectedIds.size > 0 && (
                                    <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{sendTteSpecificSelectedIds.size} dipilih</span>
                                )}
                            </div>
                        ) : (
                        <>
                        <div className="grid gap-2 min-w-0">
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tipe Sertifikat *</label>
                            <Select value={sendTteType} onValueChange={setSendTteType}>
                                <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-indigo-500">
                                    <SelectValue placeholder="Pilih Tipe" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl">
                                    <SelectItem value="convention">Konvensi</SelectItem>
                                    <SelectItem value="conference">Konferensi (Peserta & Pemakalah)</SelectItem>
                                    <SelectItem value="mainroom">Mainroom / Plenary</SelectItem>
                                    {certificateTypes?.find((c: any) => c.code === 'PRESENTER') && (
                                        <SelectItem value={"conference_presenter_" + certificateTypes.find((c: any) => c.code === 'PRESENTER')?.id}>
                                            Konferensi - Presenter (Paksa)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.find((c: any) => c.code === 'NON_PRESENTER') && (
                                        <SelectItem value={"conference_nonpresenter_" + certificateTypes.find((c: any) => c.code === 'NON_PRESENTER')?.id}>
                                            Konferensi - Non-Presenter (Paksa)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PAPER') && (
                                        <SelectItem value={"conference_bestpaper_" + certificateTypes.find((c: any) => c.code === 'BEST_PAPER')?.id}>
                                            Best Paper (Konferensi)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PRESENTER') && (
                                        <SelectItem value={"conference_bestpresenter_" + certificateTypes.find((c: any) => c.code === 'BEST_PRESENTER')?.id}>
                                            Best Presenter (Konferensi)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.filter((c: any) => c.code && c.source_type !== 'event_guest' && !['PRESENTER', 'NON_PRESENTER', 'BEST_PAPER', 'BEST_PRESENTER'].includes(c.code)).map((c: any) => (
                                        <SelectItem key={c.id} value={"speaker_" + c.id}>{c.name} (Konferensi)</SelectItem>
                                    ))}
                                    {certificateTypes?.filter((c: any) => c.source_type === 'event_guest').map((c: any) => (
                                        <SelectItem key={c.id} value={"event_guest_" + c.id}>{c.name} (Konvensi)</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {(sendTteType.startsWith('conference') || sendTteType.startsWith('speaker_')) && (
                            <div className="grid gap-2 min-w-0">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Track Konferensi</label>
                                <Select value={sendTteConferenceId} onValueChange={setSendTteConferenceId}>
                                    <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-indigo-500">
                                        <SelectValue placeholder="Semua Track Konferensi" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl max-h-64">
                                        <SelectItem value="all">Semua Track Konferensi</SelectItem>
                                        {conferencesData?.map((conf: any) => (
                                            <SelectItem key={conf.conference_id} value={String(conf.conference_id)}>
                                                {conf.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {sendTteType.startsWith('conference_bestpresenter') && (
                            <div className="grid gap-2 min-w-0">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Room (Opsional)</label>
                                <Select value={sendTteRoomId} onValueChange={setSendTteRoomId}>
                                    <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-indigo-500">
                                        <SelectValue placeholder="Semua Room" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl max-h-64">
                                        <SelectItem value="all">Semua Room</SelectItem>
                                        {tteRoomsData?.map((r: any) => (
                                            <SelectItem key={r.id} value={String(r.id)}>
                                                {r.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="grid gap-2 min-w-0">
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Limit / Batas Maksimal per Pengiriman</label>
                            <Input
                                type="number"
                                min={1}
                                max={200}
                                value={sendTteLimit}
                                onChange={(e) => setSendTteLimit(e.target.value)}
                                placeholder="Contoh: 50"
                                className="h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl"
                            />
                            <span className="text-xs text-slate-500 whitespace-normal leading-relaxed">Maksimal 200 dokumen per sekali pengiriman ke TTE. Kosongkan untuk pakai default (50).</span>
                        </div>
                        </>
                        )}
                    </div>
                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setIsSendTteModalOpen(false)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button
                            className="rounded-xl font-bold bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={confirmSendTte}
                            disabled={sendTte.isPending || (sendTteMode === 'specific' && sendTteSpecificSelectedIds.size === 0)}
                        >
                            {sendTte.isPending ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Memproses...
                                </>
                            ) : (
                                <>
                                    <FileSignature className="w-4 h-4 mr-2" />
                                    Proses TTE
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>


            {/* Send Email Modal */}
            <Dialog open={isSendEmailModalOpen} onOpenChange={setIsSendEmailModalOpen}>
                <DialogContent className="sm:max-w-[550px] whitespace-normal overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Kirim Email Sertifikat</DialogTitle>
                        <DialogDescription className="text-slate-500 dark:text-slate-400 mt-2">
                            {sendEmailMode === 'bulk'
                                ? 'Pilih tipe sertifikat yang akan dikirimi email massal. Hanya sertifikat yang sudah selesai di-TTE dan belum pernah dikirimi email yang akan diproses.'
                                : 'Cari dan pilih satu data sertifikat spesifik (nama, email, atau nomor sertifikat) yang akan dikirimi/dikirim ulang emailnya.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
                            <button
                                type="button"
                                onClick={() => setSendEmailMode('bulk')}
                                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${sendEmailMode === 'bulk' ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                                Massal (Berdasarkan Tipe)
                            </button>
                            <button
                                type="button"
                                onClick={() => setSendEmailMode('specific')}
                                className={`h-10 rounded-lg text-sm font-semibold transition-colors ${sendEmailMode === 'specific' ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                                Sertifikat Spesifik
                            </button>
                        </div>

                        {sendEmailMode === 'specific' ? (
                            <div className="grid gap-2 min-w-0">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Cari Sertifikat *</label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        value={sendEmailSpecificSearch}
                                        onChange={(e) => { setSendEmailSpecificSearch(e.target.value); setSendEmailSpecificId(null); }}
                                        placeholder="Ketik nama, email, atau nomor sertifikat..."
                                        className="pl-9 h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl"
                                    />
                                </div>
                                <div className="max-h-64 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl divide-y divide-slate-100 dark:divide-slate-800">
                                    {isSendEmailSpecificSearching ? (
                                        <div className="p-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin" /> Mencari...
                                        </div>
                                    ) : sendEmailSpecificData?.data && sendEmailSpecificData.data.length > 0 ? (
                                        sendEmailSpecificData.data.map((cert: any) => {
                                            const name = cert.type === 'convention'
                                                ? cert.registration?.participant?.full_name
                                                : cert.speaker
                                                    ? cert.speaker?.name
                                                    : cert.event_guest
                                                        ? cert.event_guest?.full_name
                                                        : cert.type === 'mainroom'
                                                            ? cert.registration?.full_name
                                                            : cert.registration?.participant_name;
                                            const email = cert.type === 'convention'
                                                ? cert.registration?.participant?.email
                                                : cert.speaker
                                                    ? cert.speaker?.email
                                                    : cert.event_guest
                                                        ? cert.event_guest?.email
                                                        : cert.registration?.email;
                                            const isSelected = sendEmailSpecificId === cert.id;
                                            return (
                                                <button
                                                    type="button"
                                                    key={cert.id}
                                                    onClick={() => setSendEmailSpecificId(cert.id)}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${isSelected ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="font-semibold text-slate-800 dark:text-slate-200 truncate">{name || 'Tanpa Nama'}</span>
                                                        {cert.is_emailed && (
                                                            <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">Sudah Dikirim</span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-slate-500 truncate">{email || '-'} &middot; {cert.certificate_type?.name || cert.type}</div>
                                                </button>
                                            );
                                        })
                                    ) : (
                                        <div className="p-4 text-center text-sm text-slate-400">
                                            Tidak ada sertifikat yang cocok (hanya sertifikat yang sudah selesai di-TTE yang ditampilkan).
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                        <>
                        <div className="grid gap-2 min-w-0">
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tipe Sertifikat *</label>
                            <Select value={sendEmailType} onValueChange={setSendEmailType}>
                                <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-indigo-500">
                                    <SelectValue placeholder="Pilih Tipe" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl">
                                    <SelectItem value="all">Semua Tipe</SelectItem>
                                    <SelectItem value="convention">Konvensi</SelectItem>
                                    <SelectItem value="conference">Konferensi (Peserta & Pemakalah)</SelectItem>
                                    <SelectItem value="mainroom">Mainroom / Plenary</SelectItem>
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PAPER') && (
                                        <SelectItem value={"conference_bestpaper_" + certificateTypes.find((c: any) => c.code === 'BEST_PAPER')?.id}>
                                            Best Paper (Konferensi)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PRESENTER') && (
                                        <SelectItem value={"conference_bestpresenter_" + certificateTypes.find((c: any) => c.code === 'BEST_PRESENTER')?.id}>
                                            Best Presenter (Konferensi)
                                        </SelectItem>
                                    )}
                                    <SelectItem value="speaker">Semua Speaker (Konferensi)</SelectItem>
                                    {certificateTypes?.filter((c: any) => c.code && c.source_type !== 'event_guest' && !['PRESENTER', 'NON_PRESENTER', 'BEST_PAPER', 'BEST_PRESENTER'].includes(c.code)).map((c: any) => (
                                        <SelectItem key={c.id} value={"speaker_" + c.id}>{c.name} (Konferensi)</SelectItem>
                                    ))}
                                    <SelectItem value="event_guest">Semua Panitia & Tamu Konvensi</SelectItem>
                                    {certificateTypes?.filter((c: any) => c.source_type === 'event_guest').map((c: any) => (
                                        <SelectItem key={c.id} value={"event_guest_" + c.id}>{c.name} (Konvensi)</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {(sendEmailType.startsWith('conference') || sendEmailType === 'speaker' || sendEmailType.startsWith('speaker_')) && (
                            <div className="grid gap-2 min-w-0">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Track Konferensi</label>
                                <Select value={sendEmailConferenceId} onValueChange={setSendEmailConferenceId}>
                                    <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-indigo-500">
                                        <SelectValue placeholder="Semua Track Konferensi" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl max-h-64">
                                        <SelectItem value="all">Semua Track Konferensi</SelectItem>
                                        {conferencesData?.map((conf: any) => (
                                            <SelectItem key={conf.conference_id} value={String(conf.conference_id)}>
                                                {conf.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {sendEmailType.startsWith('conference_bestpresenter') && (
                            <div className="grid gap-2 min-w-0">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Room (Opsional)</label>
                                <Select value={sendEmailRoomId} onValueChange={setSendEmailRoomId}>
                                    <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-indigo-500">
                                        <SelectValue placeholder="Semua Room" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl max-h-64">
                                        <SelectItem value="all">Semua Room</SelectItem>
                                        {emailRoomsData?.map((r: any) => (
                                            <SelectItem key={r.id} value={String(r.id)}>
                                                {r.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        </>
                        )}
                    </div>
                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setIsSendEmailModalOpen(false)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button
                            className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={confirmSendEmail}
                            disabled={sendEmail.isPending || (sendEmailMode === 'specific' && !sendEmailSpecificId)}
                        >
                            {sendEmail.isPending ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Memproses...
                                </>
                            ) : (
                                <>
                                    <Mail className="w-4 h-4 mr-2" />
                                    Kirim Email
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Export Data Modal */}
            <Dialog open={isExportModalOpen} onOpenChange={setIsExportModalOpen}>
                <DialogContent className="sm:max-w-[550px] whitespace-normal overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Export Data Sertifikat</DialogTitle>
                        <DialogDescription className="text-slate-500 dark:text-slate-400 mt-2">
                            Pilih tipe sertifikat dan kolom URL file yang ingin disertakan pada file Excel yang diexport.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2 min-w-0">
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tipe Sertifikat *</label>
                            <Select value={exportType} onValueChange={setExportType}>
                                <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-indigo-500">
                                    <SelectValue placeholder="Pilih Tipe" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl">
                                    <SelectItem value="all">Semua Tipe</SelectItem>
                                    <SelectItem value="convention">Konvensi</SelectItem>
                                    <SelectItem value="conference">Konferensi (Peserta & Pemakalah)</SelectItem>
                                    <SelectItem value="mainroom">Mainroom / Plenary</SelectItem>
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PAPER') && (
                                        <SelectItem value={"conference_bestpaper_" + certificateTypes.find((c: any) => c.code === 'BEST_PAPER')?.id}>
                                            Best Paper (Konferensi)
                                        </SelectItem>
                                    )}
                                    {certificateTypes?.find((c: any) => c.code === 'BEST_PRESENTER') && (
                                        <SelectItem value={"conference_bestpresenter_" + certificateTypes.find((c: any) => c.code === 'BEST_PRESENTER')?.id}>
                                            Best Presenter (Konferensi)
                                        </SelectItem>
                                    )}
                                    <SelectItem value="speaker">Semua Speaker (Konferensi)</SelectItem>
                                    {certificateTypes?.filter((c: any) => c.code && c.source_type !== 'event_guest' && !['PRESENTER', 'NON_PRESENTER', 'BEST_PAPER', 'BEST_PRESENTER'].includes(c.code)).map((c: any) => (
                                        <SelectItem key={c.id} value={"speaker_" + c.id}>{c.name} (Konferensi)</SelectItem>
                                    ))}
                                    <SelectItem value="event_guest">Semua Panitia & Tamu Konvensi</SelectItem>
                                    {certificateTypes?.filter((c: any) => c.source_type === 'event_guest').map((c: any) => (
                                        <SelectItem key={c.id} value={"event_guest_" + c.id}>{c.name} (Konvensi)</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {(exportType.startsWith('conference') || exportType === 'speaker' || exportType.startsWith('speaker_')) && (
                            <div className="grid gap-2 min-w-0">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Track Konferensi</label>
                                <Select value={exportConferenceId} onValueChange={setExportConferenceId}>
                                    <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-indigo-500">
                                        <SelectValue placeholder="Semua Track Konferensi" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl max-h-64">
                                        <SelectItem value="all">Semua Track Konferensi</SelectItem>
                                        {conferencesData?.map((conf: any) => (
                                            <SelectItem key={conf.conference_id} value={String(conf.conference_id)}>
                                                {conf.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {exportType.startsWith('conference_bestpresenter') && (
                            <div className="grid gap-2 min-w-0">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Room (Opsional)</label>
                                <Select value={exportRoomId} onValueChange={setExportRoomId}>
                                    <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:ring-indigo-500">
                                        <SelectValue placeholder="Semua Room" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl max-h-64">
                                        <SelectItem value="all">Semua Room</SelectItem>
                                        {exportRoomsData?.map((r: any) => (
                                            <SelectItem key={r.id} value={String(r.id)}>
                                                {r.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="grid gap-2 min-w-0">
                            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kolom URL File yang Disertakan *</label>
                            <div className="flex flex-col gap-2 p-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl">
                                <label className="flex items-center gap-2.5 cursor-pointer">
                                    <Checkbox
                                        checked={exportIncludeSignedUrl}
                                        onCheckedChange={(checked) => setExportIncludeSignedUrl(checked === true)}
                                    />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">URL Sertifikat Sudah TTE (<span className="font-mono text-xs">signed_file_url</span>)</span>
                                </label>
                                <label className="flex items-center gap-2.5 cursor-pointer">
                                    <Checkbox
                                        checked={exportIncludeLocalUrl}
                                        onCheckedChange={(checked) => setExportIncludeLocalUrl(checked === true)}
                                    />
                                    <span className="text-sm text-slate-700 dark:text-slate-300">URL Sertifikat Belum TTE (<span className="font-mono text-xs">local_file_path</span>)</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setIsExportModalOpen(false)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button
                            className="rounded-xl font-bold bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={confirmExport}
                            disabled={isExporting || (!exportIncludeSignedUrl && !exportIncludeLocalUrl)}
                        >
                            {isExporting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Memproses...
                                </>
                            ) : (
                                <>
                                    <Mail className="w-4 h-4 mr-2" />
                                    Mulai Export
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Batch Detail Modal */}
            <Dialog open={detailBatchId !== null} onOpenChange={(open) => !open && setDetailBatchId(null)}>
                <DialogContent className="sm:max-w-3xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Detail Batch #{detailBatchId}</DialogTitle>
                        <DialogDescription className="text-slate-500 dark:text-slate-400 mt-2">
                            Daftar dokumen sertifikat yang sedang diproses atau gagal pada batch ini.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-4 flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-white dark:bg-slate-900">
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <span className="text-sm text-slate-500 font-medium whitespace-nowrap">Tampilkan</span>
                                <Select value={detailBatchPerPage.toString()} onValueChange={(val) => { setDetailBatchPerPage(Number(val)); setDetailBatchPage(1); }}>
                                    <SelectTrigger className="w-[80px] h-10 border-slate-200 dark:border-slate-800 rounded-lg">
                                        <SelectValue placeholder="10" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[10, 20, 50].map((v) => (
                                            <SelectItem key={v} value={v.toString()}>{v}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-sm text-slate-500 font-medium whitespace-nowrap">data</span>
                            </div>

                            <div className="w-full sm:w-64 relative">
                                <Input
                                    type="text"
                                    placeholder="Cari nama, email, no reg..."
                                    value={detailBatchSearch}
                                    onChange={(e) => setDetailBatchSearch(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            setAppliedDetailBatchSearch(detailBatchSearch);
                                            setDetailBatchPage(1);
                                        }
                                    }}
                                    className="w-full h-10 pl-10 pr-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-lg focus:ring-indigo-500"
                                />
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            </div>
                        </div>

                        <div className="max-h-[50vh] overflow-y-auto pr-2 rounded-lg border border-slate-200 dark:border-slate-800 relative">
                            {isFetchingBatchCertificates && !loadingBatchCertificates && (
                                <div className="absolute inset-0 z-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center">
                                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                                </div>
                            )}
                            {loadingBatchCertificates ? (
                                <div className="py-10 text-center text-slate-400">
                                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-500" />
                                    <p className="text-sm">Memuat data dokumen...</p>
                                </div>
                            ) : batchCertificatesData?.data && batchCertificatesData.data.length > 0 ? (
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-bold uppercase text-xs">ID</th>
                                            <th className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-bold uppercase text-xs">Nama Peserta</th>
                                            <th className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-bold uppercase text-xs">Tipe</th>
                                            <th className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-bold uppercase text-xs">Status</th>
                                            <th className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-bold uppercase text-xs text-right">File</th>
                                        </tr>
                                </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {batchCertificatesData.data.map((cert: any) => (
                                            <tr key={cert.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                            <td className="px-4 py-3 font-mono text-xs">{cert.id}</td>
                                            <td className="px-4 py-3 font-medium">
                                                {cert.type === 'convention' ? cert.participant_registration?.participant?.full_name : cert.type === 'speaker' ? cert.speaker?.name : cert.type === 'event_guest' ? cert.event_guest?.full_name : cert.type === 'mainroom' ? cert.mainroom_attendance?.full_name : cert.conference_registration?.participant_name}
                                                <div className="text-xs text-slate-500 font-normal mt-0.5">
                                                    {cert.type === 'convention' ? cert.participant_registration?.registration_number : cert.type === 'speaker' ? '-' : cert.type === 'event_guest' ? cert.event_guest?.email : cert.type === 'mainroom' ? cert.mainroom_attendance?.email : cert.conference_registration?.invoice_number}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 capitalize">{cert.certificate_type?.name || cert.type}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-col gap-1 items-start">
                                                    {cert.is_completed ? (
                                                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-bold bg-emerald-50 text-emerald-600">Berhasil</span>
                                                    ) : (
                                                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-bold bg-amber-50 text-amber-600">Proses/Gagal</span>
                                                    )}
                                                    {cert.is_emailed ? (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-sky-600"><Mail className="w-3 h-3" /> Terkirim</span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400"><Mail className="w-3 h-3" /> Belum Kirim</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex flex-wrap items-center justify-end gap-1.5">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={(e) => { if(cert.local_file_url){ e.stopPropagation(); setSelectedPdfCert(cert); setPdfTab("local"); setPdfViewerOpen(true); } }}
                                                        disabled={!cert.local_file_url}
                                                        className={`font-bold text-[11px] h-7 px-2 ${cert.local_file_url ? 'text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50' : 'bg-orange-50 text-orange-400 border-orange-200/50 cursor-not-allowed opacity-70'}`}
                                                    >
                                                        <FileText className={`w-3 h-3 mr-1 ${cert.local_file_url ? 'text-slate-500' : ''}`} />
                                                        Lokal
                                                    </Button>
                                                    <Button
                                                        variant="default"
                                                        size="sm"
                                                        onClick={(e) => { if(cert.signed_file_url){ e.stopPropagation(); setSelectedPdfCert(cert); setPdfTab("tte"); setPdfViewerOpen(true); } }}
                                                        disabled={!cert.signed_file_url}
                                                        className={`font-bold text-[11px] h-7 px-2 shadow-none ${cert.signed_file_url ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200' : 'bg-orange-50 text-orange-500 border border-orange-200 cursor-not-allowed opacity-80'}`}
                                                    >
                                                        <FileSignature className="w-3 h-3 mr-1" />
                                                        TTE
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={(e) => { e.stopPropagation(); setEmailConfirmState([cert.id]); }}
                                                        disabled={!cert.is_completed}
                                                        title={cert.is_emailed ? "Kirim ulang email ke peserta ini" : "Kirim email ke peserta ini"}
                                                        className="font-bold text-[11px] h-7 px-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                                                    >
                                                        <Mail className="w-3 h-3 mr-1" />
                                                        Kirim
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                </table>
                            ) : (
                                <div className="py-10 text-center text-slate-400">
                                    <p className="text-sm">Tidak ada dokumen atau dokumen sudah dilepas dari batch ini (karena dibatalkan/gagal).</p>
                                </div>
                            )}
                        </div>

                        {batchCertificatesData && (
                            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-2">
                                <div className="text-sm text-slate-500 font-medium">
                                    Menampilkan {batchCertificatesData.from || 0} - {batchCertificatesData.to || 0} dari total <span className="font-bold text-slate-700 dark:text-slate-200">{batchCertificatesData.total}</span> data
                                </div>
                                {batchCertificatesData.last_page > 1 && (
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setDetailBatchPage(p => Math.max(1, p - 1))}
                                            disabled={detailBatchPage === 1}
                                            className="h-9 px-4 rounded-lg font-medium border-slate-200 dark:border-slate-700"
                                        >
                                            Sebelumnya
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setDetailBatchPage(p => p + 1)}
                                            disabled={detailBatchPage >= batchCertificatesData.last_page}
                                            className="h-9 px-4 rounded-lg font-medium border-slate-200 dark:border-slate-700"
                                        >
                                            Selanjutnya
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setDetailBatchId(null)}>
                            Tutup
                        </Button>
                        <Button
                            onClick={() => detailBatchId !== null && setEmailConfirmState({ batchId: detailBatchId })}
                            disabled={sendEmail.isPending}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                        >
                            {sendEmail.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                            Kirim Ulang Email (Semua)
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Konfirmasi Kirim Email */}
            <Dialog open={emailConfirmState !== null} onOpenChange={(open) => !open && setEmailConfirmState(null)}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                            <Mail className="w-5 h-5" />
                            Kirim Email Sertifikat
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            {emailConfirmState === "all" ? (
                                <>Apakah Anda yakin ingin mengirim email sertifikat secara massal ke <strong>seluruh data yang tampil</strong> (sesuai filter)? Peserta yang sebelumnya sudah pernah menerima email tidak akan dikirimi ulang kecuali dipilih secara manual.</>
                            ) : emailConfirmState && typeof emailConfirmState === "object" && "batchId" in emailConfirmState ? (
                                <>Apakah Anda yakin ingin mengirim ulang email ke <strong>seluruh sertifikat pada batch #{emailConfirmState.batchId}</strong>? Ini termasuk peserta yang <strong>sudah pernah</strong> menerima email sebelumnya — cocok dipakai setelah sertifikat di-generate & di-TTE ulang karena ada kesalahan.</>
                            ) : (
                                <>Apakah Anda yakin ingin mengirim (atau mengirim ulang) email sertifikat ke peserta ini?</>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setEmailConfirmState(null)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button
                            onClick={confirmSendEmailSingle}
                            className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                        >
                            Ya, Kirim Email
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Konfirmasi Hapus Riwayat Batch */}
            <Dialog open={deleteBatchConfirmId !== null} onOpenChange={(open) => !open && setDeleteBatchConfirmId(null)}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="text-rose-600 flex items-center gap-2">
                            <Trash2 className="w-5 h-5" />
                            Hapus Riwayat Batch
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            Apakah Anda yakin ingin menghapus riwayat batch TTE ini? Status sertifikat di dalamnya akan di-reset (dilepas dari antrean). Data yang telah dihapus tidak dapat dikembalikan.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4 gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setDeleteBatchConfirmId(null)} className="rounded-xl font-semibold border-slate-300 dark:border-slate-700">
                            Batal
                        </Button>
                        <Button
                            variant="destructive"
                            className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={() => {
                                if (deleteBatchConfirmId !== null) {
                                    deleteBatch.mutate(deleteBatchConfirmId);
                                }
                                setDeleteBatchConfirmId(null);
                            }}
                        >
                            Ya, Hapus
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
